// Session state. The access token stays in memory (lib/api.ts); on a page load the session is
// restored through the httpOnly refresh cookie.
import type { AuthenticatedResponse, MeResponse } from '@twinlink/shared';
import { create } from 'zustand';
import { queryClient } from '@web/app/queryClient';
import i18n from '@web/i18n';
import { api, refreshAccessToken, setAccessToken, setSessionExpiredHandler } from './api';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthState {
  status: Status;
  me: MeResponse | null;
  /** Restores the session on app start. */
  bootstrap: () => Promise<void>;
  /** Stores a fresh login result. */
  signIn: (session: AuthenticatedResponse) => Promise<void>;
  /** Re-reads /me (after profile, MFA, impersonation changes). */
  reload: () => Promise<MeResponse | null>;
  signOut: () => Promise<void>;
  clear: () => void;
}

const LAST_ORG_KEY = 'tl.lastOrg';
export const getLastOrgId = () => localStorage.getItem(LAST_ORG_KEY);
export const setLastOrgId = (id: string) => localStorage.setItem(LAST_ORG_KEY, id);

function applyLanguage(me: MeResponse | null): void {
  const lang = me?.user.preferences.language;
  if (lang && lang !== i18n.resolvedLanguage) void i18n.changeLanguage(lang);
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'loading',
  me: null,

  async bootstrap() {
    const token = await refreshAccessToken();
    if (!token) {
      set({ status: 'anonymous', me: null });
      return;
    }
    await get().reload();
  },

  async signIn(session) {
    setAccessToken(session.accessToken);
    queryClient.clear();
    await get().reload();
  },

  async reload() {
    try {
      const me = await api<MeResponse>('/me');
      applyLanguage(me);
      set({ status: 'authenticated', me });
      return me;
    } catch {
      get().clear();
      return null;
    }
  },

  async signOut() {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      // The session is dropped locally whatever the server answered.
    }
    get().clear();
  },

  clear() {
    setAccessToken(null);
    queryClient.clear();
    set({ status: 'anonymous', me: null });
  },
}));

setSessionExpiredHandler(() => useAuth.getState().clear());

/** Where a signed-in user belongs when no specific page was requested. */
export function landingPath(me: MeResponse): string {
  if (me.user.mustChangePassword || me.user.mfaSetupRequired) return '/setup';
  if (me.impersonation) return `/o/${me.impersonation.orgId}`;
  if (me.user.platformRole) return '/platform/orgs';
  const orgs = me.memberships.filter((m) => m.orgStatus !== 'deleted');
  const preferred = [me.user.preferences.defaultOrgId, getLastOrgId()].find((id) => id && orgs.some((o) => o.orgId === id));
  const target = preferred ?? orgs[0]?.orgId;
  // `start` sends each role to the start page its organization chose (Home by default).
  return target ? `/o/${target}/start` : '/no-access';
}
